import React, { useRef, useEffect, useState, useCallback } from 'react'
import {
  CropTransform,
  clampPos as clampPosGeo,
  coverScale,
  cropSizeFor,
  drawCrop,
} from '../utils/cropGeometry'

interface InstagramCropperProps {
  image: string
  aspect: number
  /** 회전 각도(도). 없으면 0 */
  rotation?: number
  onCropComplete: (croppedAreaPixels: {
    x: number
    y: number
    width: number
    height: number
  }) => void
  /** 재편집 복원: 초기 팬 위치 */
  initialPos?: { x: number; y: number }
  /** 재편집 복원: 초기 줌 */
  initialZoom?: number
  /** 현재 팬/줌을 부모에 보고(저장용) */
  onTransformChange?: (t: { pos: { x: number; y: number }; zoom: number }) => void
}

const InstagramCropper: React.FC<InstagramCropperProps> = ({
  image,
  aspect,
  rotation = 0,
  onCropComplete,
  initialPos,
  initialZoom,
  onTransformChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imgObjRef = useRef<HTMLImageElement | null>(null)
  const posRef = useRef({ x: 0, y: 0 })
  const zoomRef = useRef(1)
  const naturalRef = useRef({ w: 0, h: 0 })
  const containerSizeRef = useRef({ w: 0, h: 0 })
  const aspectRef = useRef(aspect)
  const rotationRef = useRef(rotation)
  const onCropRef = useRef(onCropComplete)
  const rafRef = useRef<number>(0)
  // 재편집 복원용: 초기 pos/zoom을 최초 로드 때 1회 적용
  const initialPosRef = useRef(initialPos)
  const initialZoomRef = useRef(initialZoom)
  const appliedInitialRef = useRef(false)
  const onTransformRef = useRef(onTransformChange)

  useEffect(() => { aspectRef.current = aspect }, [aspect])
  useEffect(() => { onCropRef.current = onCropComplete }, [onCropComplete])
  useEffect(() => { onTransformRef.current = onTransformChange }, [onTransformChange])

  const getCropSize = () => {
    const { w: cW, h: cH } = containerSizeRef.current
    return cropSizeFor(cW, cH, aspectRef.current)
  }

  /** 지금 상태를 공용 계산에 넘길 형태로 만든다 */
  const transform = (
    pos = posRef.current,
    zoom = zoomRef.current
  ): CropTransform => {
    const crop = getCropSize()
    return {
      cropW: crop.w,
      cropH: crop.h,
      pos,
      zoom,
      rotation: rotationRef.current,
      naturalW: naturalRef.current.w,
      naturalH: naturalRef.current.h,
    }
  }

  /**
   * 회전까지 고려해 자르기 창을 덮는 이동 한계로 가둔다.
   *
   * 예전에는 회전이 없다고 보고 가로/세로를 따로 가뒀다. 회전이 들어오면
   * 그 방식은 사진이 없는 모서리까지 허용해서 빈 곳이 드러난다.
   * (자세한 이유는 utils/cropGeometry.ts)
   */
  const clampPos = (x: number, y: number) => clampPosGeo(transform({ x, y }))

  // canvas에 이미지 그리기 — 비율 왜곡 절대 불가능
  const draw = useCallback(() => {
    const canvas = canvasRef.current
    const img = imgObjRef.current
    if (!canvas || !img) return
    const { w: cW, h: cH } = containerSizeRef.current
    if (cW === 0 || cH === 0) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = cW * dpr
    canvas.height = cH * dpr
    canvas.style.width = `${cW}px`
    canvas.style.height = `${cH}px`

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(dpr, dpr)

    // 배경색
    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--backgroundColorBg2').trim() || '#F5F0E8'
    ctx.fillRect(0, 0, cW, cH)

    // 자르기 창 안에만 그린다 (밖은 배경색 그대로)
    const crop = getCropSize()
    if (crop.w === 0 || crop.h === 0) return
    const cropX = (cW - crop.w) / 2
    const cropY = (cH - crop.h) / 2

    ctx.save()
    ctx.beginPath()
    ctx.rect(cropX, cropY, crop.w, crop.h)
    ctx.clip()
    drawCrop(ctx, img, transform(), cropX, cropY, crop.w, crop.h)
    ctx.restore()
  }, [])

  const reportCrop = () => {
    const crop = getCropSize()
    const scale = coverScale(transform())
    if (crop.w === 0 || scale === 0) return
    // 옛 형식(원본 픽셀 사각형)도 계속 보고한다 — 회전이 0일 때의 호환용.
    // 회전이 있으면 이 사각형만으로는 결과를 만들 수 없어서, 부모는
    // onTransformChange로 받은 값으로 그린다.
    const s = coverScale(transform()) * zoomRef.current
    onCropRef.current({
      x: Math.round(Math.max(0, (naturalRef.current.w - crop.w / s) / 2 - posRef.current.x / s)),
      y: Math.round(Math.max(0, (naturalRef.current.h - crop.h / s) / 2 - posRef.current.y / s)),
      width: Math.round(crop.w / s),
      height: Math.round(crop.h / s),
    })
    onTransformRef.current?.({ pos: { ...posRef.current }, zoom: zoomRef.current })
  }

  const requestDraw = () => {
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(draw)
  }

  const reset = () => {
    posRef.current = { x: 0, y: 0 }
    zoomRef.current = 1
    requestDraw()
    reportCrop()
  }

  // 최초 로드 시: 재편집 복원용 초기 pos/zoom이 있으면 1회 적용, 없으면 기본(reset).
  // 이후 비율 변경 등은 reset()으로 처리(초기값 무시).
  const applyInitialOrReset = () => {
    const ip = initialPosRef.current
    const iz = initialZoomRef.current
    if (!appliedInitialRef.current && ip && typeof iz === 'number') {
      zoomRef.current = iz
      posRef.current = clampPos(ip.x, ip.y)
      appliedInitialRef.current = true
      requestDraw()
      reportCrop()
    } else {
      reset()
    }
  }

  // 컨테이너 크기
  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const r = containerRef.current.getBoundingClientRect()
        containerSizeRef.current = { w: r.width, h: r.height }
        requestDraw()
        reportCrop()
      }
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  // aspect 변경
  useEffect(() => { reset() }, [aspect])

  // 각도 변경 — 위치는 유지하되 새 각도에서 빈 곳이 생기지 않게 다시 가둔다
  useEffect(() => {
    rotationRef.current = rotation
    posRef.current = clampPos(posRef.current.x, posRef.current.y)
    requestDraw()
    reportCrop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotation])

  // 이미지 로드
  useEffect(() => {
    const img = new Image()
    img.onload = () => {
      imgObjRef.current = img
      naturalRef.current = { w: img.naturalWidth, h: img.naturalHeight }
      applyInitialOrReset()
    }
    img.src = image
  }, [image])

  // 터치/마우스 이벤트
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    let dragging = false, pinching = false
    let sX = 0, sY = 0, sPX = 0, sPY = 0, sDist = 0, sZoom = 1

    const dist = (t: TouchList) => Math.sqrt((t[0].clientX - t[1].clientX) ** 2 + (t[0].clientY - t[1].clientY) ** 2)

    const onTS = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault(); pinching = true; dragging = false
        sDist = dist(e.touches); sZoom = zoomRef.current
      } else if (e.touches.length === 1) {
        dragging = true
        sX = e.touches[0].clientX; sY = e.touches[0].clientY
        sPX = posRef.current.x; sPY = posRef.current.y
      }
    }
    const onTM = (e: TouchEvent) => {
      e.preventDefault()
      if (pinching && e.touches.length === 2) {
        zoomRef.current = Math.max(1, Math.min(5, sZoom * (dist(e.touches) / sDist)))
        posRef.current = clampPos(posRef.current.x, posRef.current.y)
        requestDraw()
      } else if (dragging && e.touches.length === 1) {
        posRef.current = clampPos(sPX + (e.touches[0].clientX - sX), sPY + (e.touches[0].clientY - sY))
        requestDraw()
      }
    }
    const onTE = () => { dragging = false; pinching = false; reportCrop() }
    const onMD = (e: MouseEvent) => {
      e.preventDefault(); dragging = true
      sX = e.clientX; sY = e.clientY; sPX = posRef.current.x; sPY = posRef.current.y
      const onMM = (ev: MouseEvent) => {
        posRef.current = clampPos(sPX + (ev.clientX - sX), sPY + (ev.clientY - sY))
        requestDraw()
      }
      const onMU = () => {
        dragging = false; reportCrop()
        document.removeEventListener('mousemove', onMM)
        document.removeEventListener('mouseup', onMU)
      }
      document.addEventListener('mousemove', onMM)
      document.addEventListener('mouseup', onMU)
    }
    const onWh = (e: WheelEvent) => {
      e.preventDefault()
      zoomRef.current = Math.max(1, Math.min(5, zoomRef.current - e.deltaY * 0.002))
      posRef.current = clampPos(posRef.current.x, posRef.current.y)
      requestDraw(); reportCrop()
    }

    el.addEventListener('touchstart', onTS, { passive: false })
    el.addEventListener('touchmove', onTM, { passive: false })
    el.addEventListener('touchend', onTE); el.addEventListener('touchcancel', onTE)
    el.addEventListener('mousedown', onMD)
    el.addEventListener('wheel', onWh, { passive: false })
    return () => {
      el.removeEventListener('touchstart', onTS); el.removeEventListener('touchmove', onTM)
      el.removeEventListener('touchend', onTE); el.removeEventListener('touchcancel', onTE)
      el.removeEventListener('mousedown', onMD); el.removeEventListener('wheel', onWh)
    }
  }, [aspect, image])

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        background: 'var(--backgroundColorBg2, #F5F0E8)',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}
      />
    </div>
  )
}

export default InstagramCropper
